/**
 * 실시간 주차 관제 좌측 CCTV 리스트 사이드바.
 *
 * 분리 동기: 운영 현장의 CCTV 가 400 개 이상일 때 평탄한 리스트로는 원하는
 * 카메라를 찾기 어렵다. cctvId 가 `P1_B2_1_3` 형태(`P{N}_{Floor}_{Zone}_{Num}`)로
 * 일관되게 명명돼 있어 `parseRoiFilename` 유틸로 파싱이 가능하다. 이를 활용해:
 *   - 층(B2/1F/2F/...) Select
 *   - 구역(Zone) Select (선택된 층의 zone 만)
 *   - cctvId/displayName/description 부분 일치 검색
 *   - 층별 collapsible 그룹 (펼침 기본)
 *   - "X/Y" 개수 카운트
 *
 * 파싱 불가한 cctvId 는 "기타" 그룹으로 묶어서 일관성 유지.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Collapse,
  Button,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CctvConfig } from '../../../models/CctvTemplate';
import { consoleTokens } from '../../../styles/consoleTokens';
import {
  parseRoiFilename,
  uniqueFloors,
  uniqueZones,
} from '../../RoiEditorView/utils/parseRoiFilename';

interface CctvListSidebarProps {
  cctvList: CctvConfig[];
  selectedCctv: string;
  onSelect: (cctvId: string) => void;
}

const OTHER_FLOOR = '__OTHER__';

const CctvListSidebar: React.FC<CctvListSidebarProps> = ({ cctvList, selectedCctv, onSelect }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const t = consoleTokens(theme.palette.mode);

  const [search, setSearch] = useState('');
  const [floor, setFloor] = useState('');
  const [zone, setZone] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // cctv-list-filter-debounce: 입력마다 즉시 필터링하면 400+ 항목에서 입력 지연이
  // 누적된다. 검색어는 150ms debounce 후 적용.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 150);
    return () => clearTimeout(t);
  }, [search]);

  const cctvIds = useMemo(() => cctvList.map((c) => c.cctvId), [cctvList]);
  const floorOptions = useMemo(() => uniqueFloors(cctvIds), [cctvIds]);
  const zoneOptions = useMemo(() => uniqueZones(cctvIds, floor), [cctvIds, floor]);

  // 선택된 층/구역이 더 이상 유효하지 않으면 리셋 (데이터가 갱신되는 경우 대비).
  useEffect(() => {
    if (floor && !floorOptions.includes(floor) && floor !== OTHER_FLOOR) {
      setFloor('');
      setZone('');
    }
  }, [floorOptions, floor]);

  useEffect(() => {
    if (zone && !zoneOptions.includes(zone)) {
      setZone('');
    }
  }, [zoneOptions, zone]);

  /**
   * 필터 적용된 CCTV 목록. 검색은 cctvId/displayName/description 모두 부분 일치.
   * 결과는 원본 순서 유지.
   */
  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    return cctvList.filter((c) => {
      const parsed = parseRoiFilename(c.cctvId);
      if (floor) {
        if (floor === OTHER_FLOOR) {
          if (parsed) return false;
        } else if (!parsed || parsed.floor !== floor) {
          return false;
        }
      }
      if (zone) {
        if (!parsed || parsed.zone !== zone) return false;
      }
      if (term) {
        const hay = `${c.cctvId}\n${c.displayName || ''}\n${c.description || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [cctvList, debouncedSearch, floor, zone]);

  /**
   * 층별 그룹핑. cctvId 가 파싱되지 않으면 OTHER_FLOOR.
   * 그룹 키 순서는 floorOptions (자연 정렬) + 마지막에 OTHER.
   */
  const grouped = useMemo(() => {
    const map = new Map<string, CctvConfig[]>();
    filtered.forEach((c) => {
      const parsed = parseRoiFilename(c.cctvId);
      const key = parsed ? parsed.floor : OTHER_FLOOR;
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    });
    const orderedKeys: string[] = [];
    floorOptions.forEach((f) => {
      if (map.has(f)) orderedKeys.push(f);
    });
    if (map.has(OTHER_FLOOR)) orderedKeys.push(OTHER_FLOOR);
    return orderedKeys.map((k) => ({ floor: k, items: map.get(k) ?? [] }));
  }, [filtered, floorOptions]);

  const toggleCollapsed = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetFilters = () => {
    setSearch('');
    setFloor('');
    setZone('');
  };

  const isFiltering = Boolean(floor || zone || debouncedSearch.trim());
  const total = cctvList.length;
  const shown = filtered.length;

  return (
    <Box
      sx={{
        // 콘솔 셸의 레일 pane 을 그대로 채운다 — 폭은 셸이 정한다.
        width: '100%',
        flex: 1,
        bgcolor: t.panel,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {/* 필터 영역 — 고정 (스크롤 영역 위) */}
      <Stack
        spacing={1}
        sx={{
          p: 1,
          borderBottom: `1px solid ${t.lineSoft}`,
          bgcolor: t.panelSoft,
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="CCTV 검색 (ID/이름/설명)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
            <InputLabel>층</InputLabel>
            <Select
              value={floor}
              onChange={(e) => {
                setFloor(e.target.value);
                setZone('');
              }}
              label="층"
            >
              <MenuItem value="">
                <em>전체</em>
              </MenuItem>
              {floorOptions.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
              <MenuItem value={OTHER_FLOOR}>
                <em>기타</em>
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }} disabled={!floor || floor === OTHER_FLOOR}>
            <InputLabel>구역</InputLabel>
            <Select value={zone} onChange={(e) => setZone(e.target.value)} label="구역">
              <MenuItem value="">
                <em>전체</em>
              </MenuItem>
              {zoneOptions.map((z) => (
                <MenuItem key={z} value={z}>
                  {z}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Chip
            label={`${shown}/${total}`}
            size="small"
            color={isFiltering ? 'primary' : 'default'}
            variant={isFiltering ? 'filled' : 'outlined'}
          />
          {isFiltering && (
            <Button size="small" onClick={resetFilters} sx={{ minWidth: 0 }}>
              초기화
            </Button>
          )}
        </Stack>
      </Stack>

      {/* 리스트 영역 — 스크롤 */}
      <Box sx={{ overflowY: 'auto', flex: 1, p: 1 }}>
        {filtered.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              조건에 맞는 CCTV 가 없습니다.
            </Typography>
          </Box>
        ) : (
          grouped.map(({ floor: f, items }) => {
            const isCollapsed = collapsed.has(f);
            const label = f === OTHER_FLOOR ? '기타' : f;
            return (
              <Box key={f} sx={{ mb: 1 }}>
                {/* 그룹 헤더 — 클릭으로 collapse 토글 */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  onClick={() => toggleCollapsed(f)}
                  sx={{
                    px: 1,
                    py: 0.5,
                    cursor: 'pointer',
                    bgcolor: t.panelSoft,
                    borderRadius: 0.75,
                    '&:hover': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06) },
                  }}
                >
                  {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                  <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {items.length}
                  </Typography>
                </Stack>
                <Collapse in={!isCollapsed} unmountOnExit>
                  <Box sx={{ pt: 0.5 }}>
                    {items.map((cctv) => {
                      const isSelected = selectedCctv === cctv.cctvId;
                      return (
                        <Box
                          key={cctv.cctvId}
                          onClick={() => onSelect(cctv.cctvId)}
                          sx={{
                            px: 1.25,
                            py: 0.75,
                            mb: 0.25,
                            borderRadius: 0.75,
                            cursor: 'pointer',
                            borderLeft: `3px solid ${isSelected ? t.accent : 'transparent'}`,
                            bgcolor: isSelected ? t.accentSoft : 'transparent',
                            color: isSelected ? t.accent : t.ink,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              bgcolor: isSelected ? t.accentSoft : alpha(theme.palette.action.hover, 0.08),
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 600 : 500,
                              fontSize: '0.82rem',
                              fontFamily: t.mono,
                              color: 'inherit',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {cctv.cctvId}
                          </Typography>
                          {cctv.displayName && cctv.displayName !== cctv.cctvId && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                color: 'text.secondary',
                                fontSize: '0.72rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {cctv.displayName}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default CctvListSidebar;
